import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { logger } from '@/lib/logger';
import {
  mintCaptchaToken,
  requestCaptchaChallenge,
  showCaptchaTerminal,
} from '@/modules/captcha/core/broker';
import { isCaptchaRefusedBody, isCaptchaRefusedError } from '@/modules/captcha/core/detection';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Action name to mint the replacement reCAPTCHA token under. Optional. */
    recaptchaAction?: string;
    /**
     * Marks a request the customer did not initiate — a poll, a timer-driven refresh, a
     * prefetch. A refusal on one of these fails quietly instead of opening a puzzle.
     */
    captchaBackground?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    recaptchaAction?: string;
    captchaBackground?: boolean;
    /**
     * How many challenge→replay cycles this request has already spent. Stamped by the captcha
     * interceptor onto each replay config; absent on a first attempt. This is the retry counter,
     * and it lives here — on the request — deliberately: see the interceptor's invariants.
     */
    captchaAttempt?: number;
  }
}

const RECAPTCHA_HEADER = 'g-recaptcha-response';
const BODY_TOKEN_FIELD = 'recaptchaToken';

/**
 * Challenge→replay cycles a single request may spend before the dead-end popup takes over.
 *
 * Three, not one: a refused token is usually a low risk score, and the score genuinely does move
 * between mints — the second or third interaction on the page often clears it. Not unbounded,
 * because a score that is not going to recover would otherwise loop the customer forever.
 */
const MAX_CHALLENGE_ATTEMPTS = 3;

/** Cycles already spent by this request and its replay chain. */
function attemptsSpent(config: InternalAxiosRequestConfig): number {
  return config.captchaAttempt ?? 0;
}

/**
 * Rewrites the `recaptchaToken` field inside an already-serialized JSON request body.
 *
 * ## Invariants:
 * - Replaces the field only when it is ALREADY present. Why: a body that never carried a token
 *   is a body the endpoint reads the token from the header instead, and adding an unexpected
 *   field to it risks a deserialization failure on a request that was otherwise fine.
 * - A body that is not parseable JSON is handed back untouched rather than dropped. Why: the
 *   replay must still go out — form-data and blob uploads have no token field to rewrite, and
 *   losing the payload would turn a recoverable captcha refusal into a broken request.
 */
function withRefreshedBodyToken(data: unknown, token: string): unknown {
  if (typeof data !== 'string' || !data.includes(`"${BODY_TOKEN_FIELD}"`)) return data;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!parsed || typeof parsed !== 'object') return data;
    if (!(BODY_TOKEN_FIELD in parsed)) return data;
    return JSON.stringify({ ...parsed, [BODY_TOKEN_FIELD]: token });
  } catch {
    // Not JSON after all — send the original payload rather than nothing.
    return data;
  }
}

/** Internal signal that the customer dismissed the puzzle; never surfaced to a caller. */
class CaptchaChallengeAbandoned extends Error {}

async function solveAndReplay(
  client: AxiosInstance,
  config: InternalAxiosRequestConfig
): Promise<AxiosResponse> {
  const route = config.url ?? 'unknown';
  const shownAt = Date.now();

  const solved = await requestCaptchaChallenge();
  if (!solved) {
    logger.warn('Bot check dismissed — surfacing the original refusal', {
      feature: 'captcha',
      route,
      elapsedMs: Date.now() - shownAt,
    });
    throw new CaptchaChallengeAbandoned();
  }
  logger.info('Bot check solved — replaying the request', {
    feature: 'captcha',
    route,
    attempt: attemptsSpent(config) + 1,
    solveMs: Date.now() - shownAt,
  });

  const token = await mintCaptchaToken(config.recaptchaAction);
  const replay: InternalAxiosRequestConfig = {
    ...config,
    captchaAttempt: attemptsSpent(config) + 1,
  };

  if (token) {
    replay.headers.set(RECAPTCHA_HEADER, token);
    replay.data = withRefreshedBodyToken(replay.data, token);
  } else {
    logger.warn('No reCAPTCHA token available for the replay — retrying with the original', {
      feature: 'captcha',
      route,
    });
  }

  try {
    const response = await client.request(replay);
    // The replay can still come back as a shape-B refusal (HTTP 200 + output:"C"), which is not
    // an exception — log the outcome from the body, not from the absence of a throw.
    if (isCaptchaRefusedBody(response.data)) {
      logger.error('Bot check solved but the replay was refused again', undefined, {
        feature: 'captcha',
        route,
        shape: 'body',
      });
    } else {
      logger.info('Replay accepted after bot check', { feature: 'captcha', route });
    }
    return response;
  } catch (replayError) {
    logger.error('Bot check solved but the replay was refused again', replayError, {
      feature: 'captcha',
      route,
      shape: 'error',
    });
    throw replayError;
  }
}

/**
 * Attaches app-wide reCAPTCHA challenge handling to an Axios instance.
 *
 * When the API refuses a token, the customer is shown a puzzle; solving it mints a fresh token
 * and replays the identical request. Every guarded endpoint is covered without touching any of
 * the ~144 call sites that attach a reCAPTCHA header.
 *
 * ## Invariants:
 * - Hooks BOTH the fulfilled and the rejected branch. Why: the API has two refusal shapes, and
 *   only one of them is an error. Touchless payment and request-pickup answer a refused token
 *   with HTTP 200 and `output: "C"` (`TicketServiceImpl:1767, :1771, :2084, :2090`), so a
 *   handler wired only to rejections stays silent on exactly the two endpoints where a customer
 *   is being told a payment succeeded that did not.
 * - A request gets at most {@link MAX_CHALLENGE_ATTEMPTS} challenge→replay cycles, counted on the
 *   request itself (`captchaAttempt`, cloned onto each replay config) rather than in module state.
 *   Why, per-request: the budget belongs to one request and its replay chain. A module-level
 *   counter would leak across the ~144 guarded call sites — a `/rate` refusal would spend the
 *   budget a later `/pay` needs, with no boundary anyone owns on which to reset it. It would also
 *   break concurrency in the opposite direction: the broker coalesces simultaneous refusals onto
 *   ONE puzzle, so the three requests the booking screen fires in a tick would burn three
 *   attempts for a single solve and hit the dead end after one round.
 *   Why bounded at all: the cause is usually a low risk score rather than a stale token, and a
 *   score does not necessarily improve on the next mint — unbounded, the customer would be
 *   trapped in a puzzle that cannot help them.
 * - Spending the last attempt engages the terminal popup and STILL surfaces the original outcome
 *   to the caller. Why: the popup explains the dead end to the customer, but the parked request
 *   must not be left hanging — the call site's own error handling is what puts the screen into a
 *   coherent state underneath it.
 * - A request marked `captchaBackground` NEVER opens a puzzle. Why: a modal is an interruption,
 *   and it is only ever justified by something the customer just did. The QR booking screen
 *   re-reads the rate on a clock tick; before this flag, a refusal on that timer threw a puzzle
 *   onto the screen up to a minute after the customer's last action, with no visible cause —
 *   measured at a consistent 57.7s lag across five runs. The caller decides what a quiet
 *   failure means (usually: keep showing the last good value).
 * - Dismissing the puzzle re-surfaces the ORIGINAL outcome, not a synthetic one. Why: the call
 *   site's own error handling already knows how to explain a captcha refusal; substituting a
 *   different error would replace a diagnosable failure with a misleading one.
 * - Non-captcha traffic is passed through by identity. Why: every one of the ~144 existing
 *   reCAPTCHA call sites, and every unguarded request in the app, takes this path — it must be
 *   provably unchanged, which is what the pass-through test pins.
 * - Server-side rendering is skipped entirely. Why: there is no browser to render a puzzle in,
 *   and an awaited challenge that can never settle would hang the render.
 */
export function installCaptchaInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    async (response: AxiosResponse) => {
      if (typeof window === 'undefined') return response;
      const config = response.config as InternalAxiosRequestConfig;
      if (config?.captchaBackground) return response;
      if (!isCaptchaRefusedBody(response.data)) return response;

      if (attemptsSpent(config) >= MAX_CHALLENGE_ATTEMPTS) {
        logger.error('Bot check exhausted — telling the customer to reload', undefined, {
          feature: 'captcha',
          route: config?.url ?? 'unknown',
          attempts: attemptsSpent(config),
          shape: 'body',
        });
        showCaptchaTerminal();
        return response;
      }

      logger.warn('API refused the reCAPTCHA token (200 + output:C) — challenging', {
        feature: 'captcha',
        route: config?.url ?? 'unknown',
        attempt: attemptsSpent(config) + 1,
      });

      try {
        return await solveAndReplay(client, config);
      } catch (replayError) {
        // Dismissed, or the replay failed on its own terms. Hand back the refusal the caller
        // already knows how to read rather than inventing a new failure mode.
        if (replayError instanceof CaptchaChallengeAbandoned) return response;
        throw replayError;
      }
    },
    async (error: AxiosError) => {
      if (typeof window === 'undefined') return Promise.reject(error);
      const config = error.config as InternalAxiosRequestConfig | undefined;
      if (!config || config.captchaBackground) {
        return Promise.reject(error);
      }
      if (!isCaptchaRefusedError(error)) return Promise.reject(error);

      if (attemptsSpent(config) >= MAX_CHALLENGE_ATTEMPTS) {
        logger.error('Bot check exhausted — telling the customer to reload', undefined, {
          feature: 'captcha',
          route: config.url ?? 'unknown',
          attempts: attemptsSpent(config),
          shape: 'error',
        });
        showCaptchaTerminal();
        return Promise.reject(error);
      }

      logger.warn('API refused the reCAPTCHA token (400) — challenging', {
        feature: 'captcha',
        route: config.url ?? 'unknown',
        attempt: attemptsSpent(config) + 1,
      });

      try {
        return await solveAndReplay(client, config);
      } catch (replayError) {
        if (replayError instanceof CaptchaChallengeAbandoned) return Promise.reject(error);
        return Promise.reject(replayError);
      }
    }
  );
}
